import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { LoadingSpinner } from './components/LoadingSpinner';

// Pages
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { OrdersPage } from './pages/OrdersPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { ProductsPage } from './pages/ProductsPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { CustomersPage } from './pages/CustomersPage';
import { CartsPage } from './pages/CartsPage';
import { ProductViewsPage } from './pages/ProductViewsPage';
import { InventoryPage } from './pages/InventoryPage';
import { DiscountsPage } from './pages/DiscountsPage';
import { BannersPage } from './pages/BannersPage';
import { HeadersPage } from './pages/HeadersPage';
import { CarouselsPage } from './pages/CarouselsPage';
import { LandingsPage } from './pages/LandingsPage';
import { ShortLinksPage } from './pages/ShortLinksPage';
import { BusinessSearchPage } from './pages/BusinessSearchPage';
import { LeadsPage } from './pages/LeadsPage';
import { JobSearchPage } from './pages/JobSearchPage';
import { JobsPage } from './pages/JobsPage';
import { ShippingPage } from './pages/ShippingPage';
import { BlogPage } from './pages/BlogPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { AdminsPage } from './pages/AdminsPage';
import { RolesPage } from './pages/RolesPage';
import { ContactMessagesPage } from './pages/ContactMessagesPage';
import { BaleMessagesPage } from './pages/BaleMessagesPage';
import { BasalamMessagesPage } from './pages/BasalamMessagesPage';
import { BasalamProductsPage } from './pages/BasalamProductsPage';
import { PlaceholderPage } from './pages/PlaceholderPage';

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-dvh overflow-hidden bg-slate-100">
      <Sidebar />

      <div className="flex min-w-0 w-full flex-1 flex-col lg:mr-64">
        <Header />

        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-3 pb-24 sm:p-4 sm:pb-6 md:p-6">
          <div className="mx-auto w-full min-w-0 max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected Routes */}
      {isAuthenticated ? (
        <Route
          path="/*"
          element={
            <ProtectedLayout>
              <Routes>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/payments" element={<PaymentsPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/carts" element={<CartsPage />} />
                <Route path="/product-views" element={<ProductViewsPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/discounts" element={<DiscountsPage />} />
                <Route path="/banners" element={<BannersPage />} />
                <Route path="/headers" element={<HeadersPage />} />
                <Route path="/carousels" element={<CarouselsPage />} />
                <Route path="/landings" element={<LandingsPage />} />
                <Route path="/short-links" element={<ShortLinksPage />} />
                <Route path="/business-search" element={<BusinessSearchPage />} />
                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/job-search" element={<JobSearchPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/shipping" element={<ShippingPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/contact-messages" element={<ContactMessagesPage />} />
                <Route path="/bale-messages" element={<BaleMessagesPage />} />
                <Route path="/basalam-messages" element={<BasalamMessagesPage />} />
                <Route path="/basalam-products" element={<BasalamProductsPage />} />
                <Route
                  path="/reports"
                  element={
                    <PlaceholderPage
                      title="گزارش‌ها"
                      description="گزارش‌های تفصیلی فروش و درآمد"
                      icon="📊"
                    />
                  }
                />
                <Route
                  path="/content"
                  element={
                    <PlaceholderPage
                      title="محتوا"
                      description="مدیریت محتوای سایت"
                      icon="✏️"
                    />
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PlaceholderPage
                      title="تنظیمات"
                      description="تنظیمات عمومی ایوا"
                      icon="⚙️"
                    />
                  }
                />
                <Route path="/settings/admins" element={<AdminsPage />} />
                <Route path="/settings/roles" element={<RolesPage />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </ProtectedLayout>
          }
        />
      ) : (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  );
}

function AppContent() {
  return (
    <>
      <AppRoutes />
      <Toaster position="bottom-left" />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}
